import { useAdvanceBoolean, useGetProfileImage } from '../../hooks';
import { COLORS } from '../../constants/style/color';
import { InstanceContainer, NavBarLabel, NavBarProfilePic } from './navbar.styled';
import _ from 'lodash';
import useInstance from '../../hooks/custom-hooks/useInstance';
import usePermissionCheck from '../../hooks/custom-hooks/usePermissionCheck';
import Tooltip from '../tooltip';
import InstanceSwitch from './instanceSwitch';
import AlertPopup from '../alert-popup';
import useGetCustomerPersonalDetails from '../../hooks/get/useGetCustomerPersonalDetails';
import { useEffect, useState } from 'react';

interface NavLogoType {
  handleRequest: <T>(request: Promise<T>) => Promise<T | undefined>;
  data?: any;
  onSwitchModal?: () => void;
}

const NavLogo = ({ handleRequest, data, onSwitchModal }: NavLogoType): JSX.Element => {
  const { data: ProfileImage, isLoading, status } = useGetProfileImage(handleRequest);
  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
  } = useAdvanceBoolean<string>(false);

  const { data: personalDetails } = useGetCustomerPersonalDetails(handleRequest);

  const { onKillInstance, isSwitched } = useInstance({});
  const hasSwitchCompanyPermission = usePermissionCheck({
    type: 'View',
    feature: 'Customer',
    permission: 'SwitchCompany',
  });
  const hasSandboxPermission = usePermissionCheck({
    type: 'View',
    feature: 'Customer',
    permission: 'Sandbox',
  });
  const hasSwitchOrSandbox = hasSwitchCompanyPermission || hasSandboxPermission;

  const [showTooltip, setShowTooltip] = useState(window.innerWidth > 992);

  useEffect(() => {
    const handleResize = () => {
      setShowTooltip(window.innerWidth > 992);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // const hasTenant = () => {
  //   return !_.isEmpty(localStorage.getItem('tenantId'));
  // };

  if (isLoading || status !== 'success') return <></>;

  return (
    <NavBarProfilePic hasImageLogo={!_.isEmpty(ProfileImage?.content)} id='navBarProfilePic'>
      <InstanceContainer
        className='inline-flex items-center justify-center gap-2 instance'
        id='logoInstanceContainer'>
        {ProfileImage?.content && (
          <div id='companyLogoContainer'>
            {(ProfileImage?.content as unknown as boolean) && (
              <img
                src={`data:image/png;base64,${ProfileImage?.content ?? ''}`}
                alt={`${ProfileImage?.name ?? ''}`}
                id='companyLogo'
              />
            )}
          </div>
        )}
        <div
          className='flex flex-col'
          onClick={() => hasSwitchOrSandbox && onSwitchModal && onSwitchModal()}
          id='companyInfoContainer'>
          {data && (
            <>
              {showTooltip ? (
                <Tooltip
                  tooltipText={data?.companyName}
                  variant='dark'
                  size='sm'
                  placement='bottom-center'
                  id='companyNameTooltip'>
                  <span
                    className='inline-flex items-center gap-2 justify-between'
                    id='companyNameWithTooltip'>
                    <NavBarLabel
                      className='nav-switch-label'
                      variant='h5'
                      color={COLORS.DarkPurple}
                      fontWeight={700}
                      title={data?.companyName}
                      fontSize={16}
                      lineClamp={hasSwitchOrSandbox ? 1 : 2}
                      id='companyNameLabel'
                    />
                  </span>
                </Tooltip>
              ) : (
                <span
                  className='inline-flex items-center gap-2 justify-between'
                  id='companyNameWithoutTooltip'>
                  <NavBarLabel
                    className='nav-switch-label'
                    variant='h5'
                    color={COLORS.DarkPurple}
                    fontWeight={700}
                    title={data?.companyName}
                    fontSize={16}
                    lineClamp={hasSwitchOrSandbox ? 1 : 2}
                    id='companyNameLabelDirect'
                  />
                </span>
              )}
              {hasSwitchOrSandbox && <InstanceSwitch handleRequest={handleRequest} />}
            </>
          )}
        </div>
        {hasSwitchOrSandbox && data && (
          <div onClick={() => onSwitchModal && onSwitchModal()} id='switchModalTrigger'>
            <span className='material-symbols-outlined' id='switchDropdownArrow'>
              arrow_drop_down
            </span>
          </div>
        )}
        {isSwitched() && (
          <div className='cursor-pointer' onClick={openConfirmModal} id='homeButton'>
            <Tooltip
              tooltipText='Go to Home'
              variant='dark'
              size='sm'
              placement='bottom-center'
              id='homeTooltip'>
              <span
                className='material-symbols-outlined'
                style={{ color: COLORS.Purple }}
                id='homeIcon'>
                wifi_home
              </span>
            </Tooltip>
          </div>
        )}
        {isConfirmOpen && (
          <AlertPopup
            isShow={isConfirmOpen}
            closeModal={closeConfirmModal}
            customTitle={`You are about to change to ${personalDetails?.companyName} Company, Main instance. Are you sure?`}
            handleSubmit={onKillInstance}
            id='homeConfirmModal'
          />
        )}
      </InstanceContainer>
    </NavBarProfilePic>
  );
};

export { NavLogo };
